import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import DynamicTitle from "./DynamicTitle";
import WaveDivider from "./WaveDivider";
import { trackEvent } from "../utils/posthog";
import { trackInitiateCheckout } from "../utils/metaPixel";

/**
 * Landing Page: Stress Shuts Down Your Brain
 * Tied to social campaign on doomscrolling → stress → losing the ability to think.
 * Features a clip from The Behavior Panel.
 */

const BEHAVIOR_PANEL_EMBED_URL =
  "https://www.youtube.com/embed/q2b-j8c5CEs?start=1708&end=1766";

const STRIPE_LINKS = {
  monthly: "https://buy.stripe.com/5kQbJ18MweJIbT90GteME04",
  annual: "https://buy.stripe.com/fZu6oH2o89poe1hbl7eME06",
};

function StressLandingPage() {
  const location = useLocation();
  useEffect(() => {
    trackEvent("landing_page_viewed", {
      variant: "stress_brain",
      variant_name: "stress_shuts_down_brain",
      page_url: window.location.href,
    });
  }, []);

  const handleCheckoutClick = (plan) => {
    trackEvent("checkout_initiated", {
      variant: "stress_brain",
      plan_type: plan,
      source: "landing_page",
    });
    trackInitiateCheckout({ content_type: "product", content_ids: [plan] });
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <DynamicTitle>Stress Shuts Down Your Brain — RealignMind</DynamicTitle>

      <header className="bg-background py-4 px-4 border-b border-tint">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="font-bold text-2xl text-primary">
            RealignMind
          </Link>
          <Link
            to="/demo"
            state={{ from: location.pathname }}
            className="text-sm text-accent hover:text-primary font-medium"
          >
            Try Free Demo
          </Link>
        </div>
      </header>

      <main className="flex-grow">
        {/* Hero: Doomscrolling & stress */}
        <section className="bg-surfaceTint py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text mb-6 leading-tight">
              Doomscrolling Stresses You Out.
              <br />
              <span className="text-accent">And Stress Shuts Down Your Brain.</span>
            </h1>
            <p className="text-lg md:text-xl text-textSecondary mb-4 max-w-2xl mx-auto">
              Endless scrolling doesn’t just drain your time—it keeps you wound up. When
              you’re stressed all day, your ability to think clearly, focus, and make
              decisions drops.
            </p>
            <p className="text-base text-textSecondary mb-8 max-w-xl mx-auto">
              There’s a science-backed way to dial down stress and help your brain get
              back online: bilateral stimulation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/demo"
                state={{ from: location.pathname }}
                className="w-full sm:w-auto inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                Try the Free Demo
              </Link>
              <Link
                to="/subscribe"
                className="w-full sm:w-auto inline-flex items-center justify-center bg-white hover:bg-surfaceTint text-text font-bold py-4 px-8 rounded-full text-lg border border-tint transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Value props */}
            <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-textSecondary">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span><strong>Unlimited sessions</strong> — Use as often as you need</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span><strong>Privacy-first</strong> — Your data never sold</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span><strong>30-day free trial</strong> — No risk to try</span>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="transparent" bottomColor="background" />

        {/* Video: The Behavior Panel clip */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-text mb-2 text-center">
              Watch: Why Stress Shuts Down Your Brain
            </h2>
            <p className="text-textSecondary text-center mb-8 max-w-xl mx-auto">
              The Behavior Panel explains how stress affects your brain and your ability
              to think—and what you can do about it.
            </p>

            <div className="mx-auto max-w-2xl rounded-2xl overflow-hidden shadow-sm border border-tint">
              <div className="aspect-video">
                <iframe
                  src={BEHAVIOR_PANEL_EMBED_URL}
                  title="The Behavior Panel — Stress & Your Brain"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        {/* Tie-in: Stress → Bilateral stimulation */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-text mb-6 text-center">
              Counter Stress. Reclaim Your Thinking.
            </h2>
            <p className="text-textSecondary mb-6 max-w-2xl mx-auto text-center">
              Bilateral stimulation—following a gentle left-right pattern with your
              eyes or hearing alternating tones—is used in therapies like EMDR to help
              calm the nervous system and support clearer thinking.
            </p>
            <p className="text-textSecondary mb-8 max-w-2xl mx-auto text-center">
              RealignMind brings that same kind of stimulation into a simple,
              self-directed tool. No appointment needed. Use it when you’ve been
              scrolling too long, when you’re overwhelmed, or when you need to reset.
            </p>

            <ul className="space-y-3 max-w-xl mx-auto">
              {[
                "Reduce stress and mental fog",
                "Support focus and decision-making",
                "Use anytime, in private",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-textSecondary">
                  <svg
                    className="w-5 h-5 text-green-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>

            <div className="text-center mt-10">
              <Link
                to="/demo"
                state={{ from: location.pathname }}
                className="inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                Try the Free Demo
              </Link>
            </div>
          </div>
        </section>

        {/* Wellness on Your Terms - Value props */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-4">
              Wellness on Your Terms
            </h2>
            <p className="text-center text-textSecondary mb-10 max-w-2xl mx-auto">
              Access bilateral stimulation whenever you need it—when stress hits, when you've been scrolling too long, or when you need to reset.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border border-tint rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Available 24/7</h3>
                <p className="text-sm text-textSecondary">
                  Use it at 3am or during your lunch break. No scheduling, no waiting rooms.
                </p>
              </div>
              <div className="bg-white border border-tint rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Use as Often as You Need</h3>
                <p className="text-sm text-textSecondary">
                  Unlimited sessions. Daily, weekly, or whenever stress hits—it's up to you.
                </p>
              </div>
              <div className="bg-white border border-tint rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">From the Comfort of Home</h3>
                <p className="text-sm text-textSecondary">
                  No commute, no office visits. Practice in your own space, on your own time.
                </p>
              </div>
            </div>

            <div className="mt-10 bg-accent/5 rounded-xl p-6 text-center max-w-xl mx-auto border border-accent/20">
              <p className="text-textSecondary mb-2">
                <strong>Less than the cost of one therapy session</strong>
              </p>
              <p className="text-sm text-textSecondary">
                At $15/month (annual), you get unlimited access to research-backed bilateral stimulation.
              </p>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* The Science Behind Bilateral Stimulation */}
        <section className="py-12 md:py-16 bg-surfaceTint">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm">
              <div className="md:flex md:gap-12 items-center">
                <div className="md:w-2/3">
                  <h2 className="text-2xl md:text-3xl font-bold text-text mb-4">
                    The Science Behind Bilateral Stimulation
                  </h2>
                  <p className="text-textSecondary mb-4">
                    Bilateral stimulation uses rhythmic left-right sensory input—like following
                    a moving visual target with your eyes, or listening to alternating tones.
                  </p>
                  <p className="text-textSecondary mb-4">
                    Research suggests this technique can help promote relaxation, reduce stress
                    responses, and support emotional processing.
                  </p>
                  <div className="flex flex-wrap gap-4 mt-6">
                    <div className="bg-surfaceTint border border-tint px-4 py-2 rounded-lg">
                      <p className="text-xs text-textSecondary">Technique</p>
                      <p className="font-semibold text-text">Non-invasive</p>
                    </div>
                    <div className="bg-surfaceTint border border-tint px-4 py-2 rounded-lg">
                      <p className="text-xs text-textSecondary">Based on</p>
                      <p className="font-semibold text-text">Research</p>
                    </div>
                    <div className="bg-surfaceTint border border-tint px-4 py-2 rounded-lg">
                      <p className="text-xs text-textSecondary">Control</p>
                      <p className="font-semibold text-text">Always yours</p>
                    </div>
                  </div>
                </div>
                <div className="md:w-1/3 mt-8 md:mt-0">
                  <div className="rounded-xl overflow-hidden shadow-sm border border-tint">
                    <img
                      src="/images/Dot.gif"
                      alt="Bilateral stimulation - dot moving left to right"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-center text-xs text-textSecondary mt-2">
                    Simply follow the dot with your eyes
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Features */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-4">
              Everything You Need for Self-Directed Wellness
            </h2>
            <p className="text-center text-textSecondary mb-12 max-w-2xl mx-auto">
              A complete toolkit designed for when stress gets in the way
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white border border-tint rounded-xl p-6 hover:border-tintHover transition-colors">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Guided Sessions</h3>
                <p className="text-sm text-textSecondary">
                  Follow structured protocols for specific wellness goals.
                </p>
              </div>
              <div className="bg-white border border-tint rounded-xl p-6 hover:border-tintHover transition-colors">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Fully Customizable</h3>
                <p className="text-sm text-textSecondary">
                  Adjust speed, colors, sounds, and timing to find what works best for you.
                </p>
              </div>
              <div className="bg-white border border-tint rounded-xl p-6 hover:border-tintHover transition-colors">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Progress Tracking</h3>
                <p className="text-sm text-textSecondary">
                  Monitor your mood before and after sessions. See patterns over time.
                </p>
              </div>
              <div className="bg-white border border-tint rounded-xl p-6 hover:border-tintHover transition-colors">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Privacy Protected</h3>
                <p className="text-sm text-textSecondary">
                  Your data stays private. We never sell your information. Ever.
                </p>
              </div>
              <div className="bg-white border border-tint rounded-xl p-6 hover:border-tintHover transition-colors">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Crisis Resources</h3>
                <p className="text-sm text-textSecondary">
                  Built-in safety features with immediate access to crisis support resources.
                </p>
              </div>
              <div className="bg-white border border-tint rounded-xl p-6 hover:border-tintHover transition-colors">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-text mb-2">Session History</h3>
                <p className="text-sm text-textSecondary">
                  Review your past sessions and understand what's working for you.
                </p>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Pricing */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-2">
              Simple, Transparent Pricing
            </h2>
            <p className="text-center text-textSecondary mb-10">
              Start free. Cancel anytime. No hidden fees.
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-gray-300 transition-colors">
                <h3 className="text-xl font-bold text-text mb-2">Monthly</h3>
                <p className="text-textSecondary text-sm mb-4">Flexible month-to-month</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-text">$19</span>
                  <span className="text-textSecondary">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm text-textSecondary">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    7-day free trial
                  </li>
                  <li className="flex items-center gap-2 text-sm text-textSecondary">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Unlimited sessions
                  </li>
                  <li className="flex items-center gap-2 text-sm text-textSecondary">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Cancel anytime
                  </li>
                </ul>
                <a
                  href={STRIPE_LINKS.monthly}
                  onClick={() => handleCheckoutClick("monthly")}
                  className="block w-full text-center bg-background hover:bg-surfaceTint text-text font-semibold py-3 rounded-full border border-tint transition-colors"
                >
                  Start 7-Day Trial
                </a>
              </div>

              <div className="bg-gradient-to-br from-accent to-primary rounded-2xl p-8 text-white relative">
                <div className="absolute -top-3 right-4 bg-secondary text-white text-xs font-bold px-3 py-1 rounded-full">
                  SAVE $48
                </div>
                <h3 className="text-xl font-bold mb-2">Annual</h3>
                <p className="text-white/80 text-sm mb-4">Best value for committed wellness</p>
                <div className="mb-2">
                  <span className="text-4xl font-bold">$15</span>
                  <span className="text-white/80">/month</span>
                </div>
                <p className="text-sm text-white/80 mb-6">
                  <span className="line-through opacity-75">$228</span> $180/year
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    <strong>30-day free trial</strong>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Unlimited sessions
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Save $48/year
                  </li>
                </ul>
                <a
                  href={STRIPE_LINKS.annual}
                  onClick={() => handleCheckoutClick("annual")}
                  className="block w-full text-center bg-white text-accent hover:bg-surfaceTint font-bold py-3 rounded-full transition-colors"
                >
                  Start Free Trial
                </a>
              </div>
            </div>

            <div className="text-center mt-8">
              <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-6 py-3 rounded-full">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong>30-Day Money-Back Guarantee</strong> — Not satisfied? Full refund, no questions.</span>
              </div>
              <p className="text-center text-sm text-textSecondary mt-4 px-2">
                Money tight? If cost is a consideration at all, email{" "}
                <a href="mailto:access@realignmind.com?subject=Financial%20Assistance%20Request" className="text-accent hover:underline font-medium">
                  access@realignmind.com
                </a>{" "}
                and we'll get you access.
              </p>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* FAQ */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-10">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              <details className="bg-surfaceTint border border-tint rounded-xl p-6 group">
                <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-text">
                  Is RealignMind a replacement for therapy?
                  <svg className="w-5 h-5 text-textSecondary group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  No. RealignMind is a self-directed wellness tool designed to complement your overall
                  well-being routine. It does not diagnose, treat, cure, or prevent any disease or medical
                  condition. If you're experiencing mental health challenges, please consult a healthcare professional.
                </p>
              </details>

              <details className="bg-surfaceTint border border-tint rounded-xl p-6 group">
                <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-text">
                  How does the free trial work?
                  <svg className="w-5 h-5 text-textSecondary group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  You get full access to all features during your trial (7 days for monthly, 30 days for annual).
                  No charge until your trial ends. Cancel anytime before then and you won't be charged at all.
                </p>
              </details>

              <details className="bg-surfaceTint border border-tint rounded-xl p-6 group">
                <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-text">
                  Is my data private and secure?
                  <svg className="w-5 h-5 text-textSecondary group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  Absolutely. We never sell your data to third parties. Your session information is private
                  and protected. Read our full{" "}
                  <Link to="/privacy-policy" className="text-accent hover:underline">privacy policy</Link> for details.
                </p>
              </details>

              <details className="bg-surfaceTint border border-tint rounded-xl p-6 group">
                <summary className="flex justify-between items-center cursor-pointer list-none font-semibold text-text">
                  What if it doesn't work for me?
                  <svg className="w-5 h-5 text-textSecondary group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-4 text-textSecondary">
                  We offer a 30-day money-back guarantee even after your trial ends. If you're not satisfied,
                  contact us for a full refund—no questions asked.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* CTA block */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-surfaceTint rounded-3xl p-8 md:p-12 border border-tint text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-text mb-2">
                Ready to Turn Down the Stress?
              </h2>
              <p className="text-textSecondary mb-6">
                Start your free trial. No credit card required for the demo. 30-day money-back guarantee.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={STRIPE_LINKS.annual}
                  onClick={() => handleCheckoutClick("annual")}
                  className="inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
                >
                  Start Free Trial
                </a>
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center justify-center bg-white hover:bg-surfaceTint text-text font-bold py-4 px-8 rounded-full text-lg border border-tint transition-colors"
                >
                  The Science
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Minimal Footer */}
        <footer className="bg-secondary text-white py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <nav className="flex flex-wrap justify-center gap-6 mb-4">
              <Link to="/" className="text-white/80 hover:text-white transition-colors">
                Home
              </Link>
              <Link
                to="/what-is-bilateral-stimulation"
                className="text-white/80 hover:text-white transition-colors"
              >
                What is Bilateral Stimulation?
              </Link>
              <Link to="/demo" state={{ from: location.pathname }} className="text-white/80 hover:text-white transition-colors">
                Demo
              </Link>
              <Link
                to="/subscribe"
                className="text-white/80 hover:text-white transition-colors"
              >
                Start Free Trial
              </Link>
              <Link
                to="/privacy-policy"
                className="text-white/80 hover:text-white transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-white/80 hover:text-white transition-colors"
              >
                Terms
              </Link>
              <Link
                to="/emergency-support"
                className="text-white/80 hover:text-white transition-colors"
              >
                Crisis Help
              </Link>
            </nav>
            <div className="mt-6 pt-6 border-t border-gray-800 text-center">
              <p className="text-xs text-textSecondary">
                RealignMind is a wellness tool and does not diagnose, treat, cure, or
                prevent any disease or medical condition. This is not a substitute for
                professional medical advice, diagnosis, or treatment.
              </p>
            </div>
          </div>
        </footer>
      </main>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden z-50 shadow-sm">
        <div className="flex gap-3">
          <a
            href={STRIPE_LINKS.annual}
            onClick={() => handleCheckoutClick("annual")}
            className="flex-1 text-center bg-accent hover:bg-accentDark text-white font-bold py-3 px-4 rounded-full transition-colors"
          >
            Start Free Trial
          </a>
          <Link
            to="/demo"
            state={{ from: location.pathname }}
            className="text-center bg-background hover:bg-surfaceTint text-text font-semibold py-3 px-4 rounded-full border border-tint transition-colors"
          >
            Demo
          </Link>
        </div>
      </div>

      {/* Spacer for sticky CTA on mobile */}
      <div className="h-20 md:hidden" />
    </div>
  );
}

export default StressLandingPage;
